// src/AdminSocialTab.js
// Admin dashboard "Social Media" tab. Talks only to the socialAdmin endpoint
// (action-routed). Phase 1 sub-views: Overview, Queue, Compose, Settings.
import React, { useState, useEffect, useCallback } from 'react';
import { authedFetch } from './utils/authedFetch';
import { apiUrl } from './utils/apiBase';

const PLATFORMS = ['facebook', 'instagram', 'twitter', 'tiktok'];
const PLATFORM_LABEL = { facebook: 'Facebook', instagram: 'Instagram', twitter: 'X', tiktok: 'TikTok' };
const STATUS_COLORS = {
  posted: '#22c55e', partial: '#f59e0b', queued: '#3b82f6', draft: '#9ca3af',
  sending: '#6366f1', failed: '#ef4444', expired: '#a855f7', canceled: '#6b7280',
};

const socialGet = async (action, params = {}) => {
  const qs = new URLSearchParams({ action, ...params }).toString();
  const res = await authedFetch(`${apiUrl('socialAdmin')}?${qs}`);
  return res.json();
};
const socialPost = async (body) => {
  const res = await authedFetch(apiUrl('socialAdmin'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return res.json();
};

const AdminSocialTab = () => {
  const [view, setView] = useState('overview');
  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h2>Social Media</h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['overview', 'queue', 'compose', 'settings'].map((v) => (
            <button key={v} onClick={() => setView(v)}
              style={v === view ? pillActiveStyle : pillStyle}>
              {v[0].toUpperCase() + v.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {view === 'overview' && <OverviewView />}
      {view === 'queue' && <QueueView />}
      {view === 'compose' && <ComposeView />}
      {view === 'settings' && <SettingsView />}
    </div>
  );
};

/* ----------------------------- Overview ----------------------------- */

const OverviewView = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await socialGet('overview');
      if (r.success) { setData(r.data); setError(null); } else setError(r.error);
    } catch (e) { setError('Failed to load overview'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleKill = async () => {
    const next = !data.killSwitch;
    if (next && !window.confirm('Turn the kill switch ON? This halts ALL social posting.')) return;
    await socialPost({ action: 'saveSettings', settings: { killSwitch: next } });
    load();
  };

  if (loading) return <div style={loadingStyle}>Loading…</div>;
  if (error) return <ErrorBox msg={error} onRetry={load} />;

  const c = data.counts || {};
  return (
    <div>
      {!data.lateConfigured && (
        <div style={warnBanner}>
          ⚠️ Late API key not configured. Add <code>LATE_API_KEY</code> to functions/.env and
          connect your accounts in the Late dashboard. Posts will queue but cannot send until then.
        </div>
      )}

      <div style={{ ...sectionStyle, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>Master Kill Switch</div>
          <div style={{ color: '#666', fontSize: '0.9rem' }}>
            {data.killSwitch ? 'Posting is HALTED.' : 'Posting is active.'}
          </div>
        </div>
        <button onClick={toggleKill}
          style={{ ...refreshButtonStyle, backgroundColor: data.killSwitch ? '#ef4444' : '#22c55e' }}>
          {data.killSwitch ? 'Turn OFF (resume)' : 'Turn ON (halt)'}
        </button>
      </div>

      <div style={gridStyle}>
        {['queued', 'draft', 'posted', 'partial', 'failed', 'expired'].map((s) => (
          <div key={s} style={cardStyle}>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: STATUS_COLORS[s] }}>{c[s] || 0}</div>
            <div style={{ color: '#666', textTransform: 'capitalize' }}>{s}</div>
          </div>
        ))}
      </div>

      <div style={sectionStyle}>
        <h3>Connected Accounts</h3>
        {(!data.accounts || data.accounts.length === 0) ? (
          <div style={{ color: '#666' }}>No accounts connected yet.</div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.5rem' }}>
            {data.accounts.map((a, i) => (
              <span key={i} style={chipStyle}>
                {PLATFORM_LABEL[a.platform] || a.platform}
                {a.username ? ` · ${a.username}` : ''} {a.isActive ? '🟢' : '🔴'}
              </span>
            ))}
          </div>
        )}
      </div>

      <div style={sectionStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3>Recent Posts</h3>
          <button onClick={load} style={refreshButtonStyle}>Refresh</button>
        </div>
        <PostList posts={data.recent || []} onChange={load} />
      </div>
    </div>
  );
};

/* ------------------------------- Queue ------------------------------ */

const QueueView = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const r = await socialGet('list', filter ? { status: filter } : {});
    if (r.success) setPosts(r.data);
    setLoading(false);
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div style={sectionStyle}>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', alignItems: 'center' }}>
        <span>Filter:</span>
        <select value={filter} onChange={(e) => setFilter(e.target.value)} style={inputStyle}>
          <option value="">All</option>
          {['queued', 'draft', 'posted', 'partial', 'failed', 'expired', 'canceled'].map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <button onClick={load} style={refreshButtonStyle}>Refresh</button>
      </div>
      {loading ? <div style={loadingStyle}>Loading…</div> : <PostList posts={posts} onChange={load} />}
    </div>
  );
};

const PostList = ({ posts, onChange }) => {
  if (!posts.length) return <div style={{ color: '#666', padding: '1rem 0' }}>No posts.</div>;
  const act = async (action, id) => { await socialPost({ action, id }); onChange(); };
  return (
    <div style={{ marginTop: '0.5rem' }}>
      {posts.map((p) => (
        <div key={p.id} style={postRowStyle}>
          <div style={{ flex: 1 }}>
            <div style={{ marginBottom: '0.25rem' }}>
              <span style={{ ...badgeStyle, backgroundColor: STATUS_COLORS[p.status] || '#999' }}>{p.status}</span>
              <span style={{ marginLeft: '0.5rem', fontWeight: 600 }}>{p.type}</span>
              {p.drawId && <span style={{ color: '#666', marginLeft: '0.5rem' }}>{p.lottery} {p.drawId}</span>}
              <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.85rem' }}>
                {(p.platforms || []).map((x) => PLATFORM_LABEL[x] || x).join(', ')}
              </span>
            </div>
            <div style={{ color: '#444', fontSize: '0.9rem', whiteSpace: 'pre-wrap' }}>
              {(p.caption || '').slice(0, 220)}{(p.caption || '').length > 220 ? '…' : ''}
            </div>
            {p.lastError && <div style={{ color: '#ef4444', fontSize: '0.8rem', marginTop: '0.25rem' }}>{p.lastError}</div>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
            {['draft', 'failed', 'expired', 'canceled'].includes(p.status) && (
              <button onClick={() => act('retry', p.id)} style={smallBtn}>Post now</button>
            )}
            {['queued', 'draft'].includes(p.status) && (
              <button onClick={() => act('cancel', p.id)} style={{ ...smallBtn, backgroundColor: '#ef4444' }}>Cancel</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ------------------------------ Compose ----------------------------- */

const ComposeView = () => {
  const [caption, setCaption] = useState('');
  const [platforms, setPlatforms] = useState(['facebook', 'instagram', 'twitter']);
  const [scheduledFor, setScheduledFor] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = (p) => setPlatforms((ps) => ps.includes(p) ? ps.filter((x) => x !== p) : [...ps, p]);

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      const body = { action: 'compose', caption, platforms };
      if (scheduledFor) body.scheduledFor = new Date(scheduledFor).getTime();
      const r = await socialPost(body);
      if (r.success) { setMsg({ ok: true, text: `Queued (${r.data.id}).` }); setCaption(''); }
      else setMsg({ ok: false, text: r.error });
    } catch (e) { setMsg({ ok: false, text: 'Request failed' }); } finally { setBusy(false); }
  };

  return (
    <div style={sectionStyle}>
      <h3>Compose a Post</h3>
      <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={5}
        placeholder="Write your caption…" style={{ ...inputStyle, width: '100%', resize: 'vertical' }} />
      <div style={{ margin: '1rem 0' }}>
        <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>Platforms</div>
        {PLATFORMS.map((p) => (
          <label key={p} style={{ marginRight: '1rem' }}>
            <input type="checkbox" checked={platforms.includes(p)} onChange={() => toggle(p)} /> {PLATFORM_LABEL[p]}
          </label>
        ))}
      </div>
      <div style={{ marginBottom: '1rem' }}>
        <span style={{ marginRight: '0.5rem' }}>Schedule (optional):</span>
        <input type="datetime-local" value={scheduledFor} onChange={(e) => setScheduledFor(e.target.value)} style={inputStyle} />
        <span style={{ color: '#666', marginLeft: '0.5rem', fontSize: '0.85rem' }}>Leave blank to post now.</span>
      </div>
      <button onClick={submit} disabled={busy || !caption || !platforms.length} style={refreshButtonStyle}>
        {busy ? 'Queuing…' : 'Queue Post'}
      </button>
      {msg && <div style={{ marginTop: '1rem', color: msg.ok ? '#22c55e' : '#ef4444' }}>{msg.text}</div>}
    </div>
  );
};

/* ------------------------------ Settings ---------------------------- */

const TYPE_LABELS = {
  results: 'Winning Numbers (results)',
  performance: 'Member Performance',
  marketing: 'Marketing / Promo',
};

const SettingsView = () => {
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    const r = await socialGet('settings');
    if (r.success) setCfg(r.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!cfg) return <div style={loadingStyle}>Loading…</div>;

  const updateType = (t, patch) =>
    setCfg((c) => ({ ...c, types: { ...c.types, [t]: { ...c.types[t], ...patch } } }));
  const togglePlatform = (t, p) => {
    const cur = cfg.types[t].platforms || [];
    updateType(t, { platforms: cur.includes(p) ? cur.filter((x) => x !== p) : [...cur, p] });
  };

  const save = async () => {
    const r = await socialPost({ action: 'saveSettings', settings: cfg });
    if (r.success) { setCfg(r.data); setSaved(true); setTimeout(() => setSaved(false), 2000); }
  };

  return (
    <div>
      <div style={warnBanner}>
        Compliance reminder: lottery content is gambling-adjacent. Keep marketing posts organic —
        never boost them as ads. Results posts are lowest-risk; performance/marketing need careful
        framing (no win guarantees).
      </div>
      {['results', 'performance', 'marketing'].map((t) => (
        <div key={t} style={sectionStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>{TYPE_LABELS[t]}</h3>
            <label>
              <input type="checkbox" checked={!!cfg.types[t].enabled}
                onChange={(e) => updateType(t, { enabled: e.target.checked })} /> Enabled
            </label>
          </div>
          <div style={{ margin: '0.75rem 0' }}>
            <span style={{ marginRight: '0.5rem' }}>Mode:</span>
            <select value={cfg.types[t].mode} onChange={(e) => updateType(t, { mode: e.target.value })} style={inputStyle}>
              <option value="auto">Auto-post</option>
              <option value="draft">Draft (review first)</option>
            </select>
          </div>
          <div>
            <span style={{ marginRight: '0.5rem' }}>Platforms:</span>
            {PLATFORMS.map((p) => (
              <label key={p} style={{ marginRight: '1rem' }}>
                <input type="checkbox" checked={(cfg.types[t].platforms || []).includes(p)}
                  onChange={() => togglePlatform(t, p)} /> {PLATFORM_LABEL[p]}
              </label>
            ))}
          </div>
        </div>
      ))}
      <div style={sectionStyle}>
        <label>
          Marketing jackpot threshold ($):{' '}
          <input type="number" value={cfg.marketingJackpotThreshold || 0}
            onChange={(e) => setCfg((c) => ({ ...c, marketingJackpotThreshold: parseInt(e.target.value, 10) || 0 }))}
            style={{ ...inputStyle, width: '160px' }} />
        </label>
        <div style={{ color: '#666', fontSize: '0.85rem', marginTop: '0.25rem' }}>
          Marketing posts only fire when the jackpot is at or above this amount.
        </div>
      </div>
      <button onClick={save} style={refreshButtonStyle}>Save Settings</button>
      {saved && <span style={{ marginLeft: '1rem', color: '#22c55e' }}>Saved ✓</span>}
    </div>
  );
};

/* ------------------------------- bits ------------------------------- */

const ErrorBox = ({ msg, onRetry }) => (
  <div style={errorStyle}><p>Error: {msg}</p><button onClick={onRetry} style={refreshButtonStyle}>Retry</button></div>
);

/* ------------------------------ styles ------------------------------ */

const containerStyle = { padding: '2rem', maxWidth: '1200px', margin: '0 auto' };
const headerStyle = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '2px solid #e5e7eb' };
const sectionStyle = { backgroundColor: 'white', padding: '1.5rem', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', marginBottom: '1.5rem' };
const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', marginBottom: '1.5rem' };
const cardStyle = { textAlign: 'center', padding: '1.25rem', backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.08)' };
const pillStyle = { padding: '0.4rem 0.9rem', backgroundColor: '#e5e7eb', color: '#333', border: 'none', borderRadius: '20px', cursor: 'pointer' };
const pillActiveStyle = { ...pillStyle, backgroundColor: '#1976d2', color: 'white' };
const refreshButtonStyle = { padding: '0.5rem 1rem', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' };
const smallBtn = { padding: '0.3rem 0.6rem', backgroundColor: '#1976d2', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.8rem' };
const inputStyle = { padding: '0.4rem', borderRadius: '5px', border: '1px solid #ccc', fontSize: '0.9rem' };
const chipStyle = { padding: '0.4rem 0.75rem', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '20px', fontSize: '0.9rem' };
const badgeStyle = { color: 'white', padding: '0.15rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', textTransform: 'uppercase' };
const postRowStyle = { display: 'flex', gap: '1rem', padding: '0.9rem', borderBottom: '1px solid #eee', alignItems: 'flex-start' };
const warnBanner = { backgroundColor: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', padding: '1rem', borderRadius: '8px', marginBottom: '1.5rem', fontSize: '0.9rem', lineHeight: 1.5 };
const loadingStyle = { padding: '2rem', textAlign: 'center', fontSize: '1.1rem' };
const errorStyle = { padding: '2rem', textAlign: 'center', color: '#d32f2f' };

export default AdminSocialTab;
